import React from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { useTimezone } from '../contexts/TimezoneContext';
import './TimezoneIndicator.css';

interface TimezoneIndicatorProps {
	className?: string;
	showIndicator?: boolean;
	customMessage?: string | null;
}

/**
 * Get timezone abbreviation (e.g., PST, EST, CDT) for a given timezone
 * Uses the visitor's locale for proper localization
 */
function getTimezoneAbbreviation(timezone: string): string {
	try {
		// Use the visitor's locale (or default to 'en-US' if not available)
		const locale = navigator.language || 'en-US';

		// Create a date formatter for the timezone
		const formatter = new Intl.DateTimeFormat(locale, {
			timeZone: timezone,
			timeZoneName: 'short',
		});

		// Format a date and extract the timezone abbreviation
		const parts = formatter.formatToParts(new Date());

		const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

		return timeZonePart?.value || timezone;
	} catch (e) {
		return timezone;
	}
}

export const TimezoneIndicator: React.FC<TimezoneIndicatorProps> = ({
	className = '',
	showIndicator = true,
	customMessage = null,
}) => {
	const {
		mode,
		siteTimezone,
		localTimezone,
		siteTimezoneLabel,
		localTimezoneLabel,
		siteTimezoneOffset,
		localTimezoneOffset,
	} = useTimezone();

	// Don't show indicator if disabled
	if (!showIndicator) {
		return null;
	}

	// When in local (user) timezone mode, always show the indicator so users
	// see confirmation that times are displayed in their timezone.
	// When in site timezone mode, only show if timezones differ.
	if (
		mode === 'site' &&
		(siteTimezone === localTimezone ||
			siteTimezoneLabel === localTimezoneLabel ||
			siteTimezoneOffset === localTimezoneOffset)
	) {
		return null;
	}

	const currentTimezone = mode === 'site' ? siteTimezone : localTimezone;
	const timezoneAbbr = getTimezoneAbbreviation(currentTimezone);

	let message: string;

	if (customMessage) {
		// When in local mode, replace {timezone} with "your timezone (abbr)"
		const replacement =
			mode === 'local'
				? sprintf(
						/* translators: %s is the timezone abbreviation, e.g., EST */
						__('your timezone (%s)', 'gp-bookings'),
						timezoneAbbr
					)
				: timezoneAbbr;
		message = customMessage.replace('{timezone}', replacement);
	} else if (mode === 'local') {
		message = sprintf(
			/* translators: %s is the timezone abbreviation, e.g., EST */
			__('All times shown in your timezone (%s)', 'gp-bookings'),
			timezoneAbbr
		);
	} else {
		message = sprintf(
			/* translators: %s is the timezone abbreviation, e.g., PST, EST, CDT */
			__('All times shown in %s', 'gp-bookings'),
			timezoneAbbr
		);
	}

	return (
		<div className={`gpb-timezone-indicator ${className}`}>
			<small>{message}</small>
		</div>
	);
};
